import { misc } from "@/common/theme";
import ShaderObject from "@/mono/components/gl/ShaderObject";
import {
  gaussianBlur,
  matcap,
  roundBorders,
  transformUV,
} from "@/mono/lib/gl/shaderChunks";
import { useFrame } from "@react-three/fiber";
import { useEffect, useMemo, useRef } from "react";
import { Color, PlaneGeometry, RepeatWrapping, Vector2 } from "three";

import { useThemeTransitionGLUniforms } from "@/lib/useThemeTransitionGL";
import useResize from "@/mono/lib/hooks/useResize";
import { useTexture } from "@react-three/drei";
import { useTheme } from "styled-components";
import { howWeDoItRenderTexture } from "./HowWeDoItScene";

import noiseTex2 from "@/assets/blue_noise_rgba_1024.jpg";
import noiseTex from "@/assets/rgba-noise-medium.png";

// @refresh reset

const vertexShader = /*glsl*/ `
  varying vec2 vUv;
  varying vec3 vEye;
  varying vec3 vNormal;
  varying vec3 vPos;

  void main() {
    mat4 LM = modelMatrix;
    LM[2][3] = 0.0;
    LM[3][0] = 0.0;
    LM[3][1] = 0.0;
    LM[3][2] = 0.0;

    vec4 GN = LM * vec4(normal.xyz, 1.0);
    vNormal = normalize(GN.xyz);
    vEye = normalize(GN.xyz-cameraPosition);

    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    gl_Position = projectionMatrix * mvPosition;

    vUv = uv;
    vPos = (modelMatrix * vec4(position, 1.)).xyz;
    //vEye = mvPosition.xyz;
    // vNormal = normal;
  }
`;

const fragmentShader = /*glsl*/ `
  varying vec2 vUv;
  varying vec3 vEye;
  varying vec3 vNormal;
  varying vec3 vPos;

  uniform vec2 uSize;
  uniform vec2 uResolution;
  uniform vec3 uBorderColor;
  
  uniform sampler2D tScene;
  uniform sampler2D tScenePrev;
  // uniform sampler2D tBackgroundColor;
  // uniform sampler2D tMatcap;
  // uniform sampler2D tNormal;

  uniform sampler2D tBlurNoise;
  uniform sampler2D tBlurNoise2;

  ${roundBorders}
  ${transformUV}
  ${gaussianBlur}

   float stepfun(float x) {
    return (sign(x) + 1.0) / 2.0;
  }

  float square(vec2 pos) {
      return (stepfun(pos.x + 1.0) * stepfun(1.0 - pos.x)) *
          (stepfun(pos.y + 1.0) * stepfun(1.0 - pos.y));
  }

  vec2 dist(vec2 pos) {
    vec2 offset = pos;
    vec2 noise = texture2D(tBlurNoise, (offset) * 40.0).xy * 0.1;
    vec2 noise2 = texture2D(tBlurNoise2, (offset) * 50.0).xy * 0.05;
    return pos + square(offset) * noise + noise2;
  }

  // Converts a color from linear light gamma to sRGB gamma
  vec3 fromLinear(vec3 linearRGB)
  {
      bvec3 cutoff = lessThan(linearRGB.rgb, vec3(0.0031308));
      vec3 higher = vec3(1.055)*pow(linearRGB.rgb, vec3(1.0/2.4)) - vec3(0.055);
      vec3 lower = linearRGB.rgb * vec3(12.92);

      return mix(higher, lower, cutoff);
  }

  // Converts a color from sRGB gamma to linear light gamma
  vec3 toLinear(vec3 sRGB) {
      bvec3 cutoff = lessThan(sRGB.rgb, vec3(0.04045));
      vec3 higher = pow((sRGB.rgb + vec3(0.055))/vec3(1.055), vec3(2.4));
      vec3 lower = sRGB.rgb/vec3(12.92);

      return mix(higher, lower, cutoff);
  }


  vec3 gammaCorrect(vec3 col) {
      return pow(col, vec3(.4545));
  }

  vec3 getSceneColor(vec2 uv, vec2 pixel) {
    float darken = 0.9;
    
    // Get our normal warp
    vec2 warp = dist(uv);
    vec3 blurred = blur9(tScene, dist(uv), uResolution * 0.125, vec2(2.)).rgb;
    vec3 blurred2 = texture2D(tScene, dist(uv)).rgb;
    
    blurred = mix(blurred, blurred2, 0.5);
    
    return gammaCorrect(blurred) * darken;
  }

  ${matcap}

  void main() {
    float a = 1.0;
    float radius = ${misc.gl.borderRadius};

    

    vec2 uv = vUv;
    //roundedBorder(0.0, radius, uv, vec2(uSize), a);  

    vec2 st = gl_FragCoord.xy / uResolution;
    vec2 pixel = 1.0 / (uResolution);   

    // Bit hacky but works?
    vec2 uuv = vec2(st.x, vUv.y); 
    uuv.x -= 0.0635;
    // HINT: needs to correction to uv.y, lower down this value should be less
    uuv.y *= pow(0.84, vUv.y * 1.1);

    //uuv = scaleUV(uuv, vec2(1.04), vec2(0.5));
    vec3 color = getSceneColor(uuv, pixel);

    float thickness = 1.;
    float inner = 0.;
    float border = roundedBorder(thickness * 1., radius * 2., uv, vec2(uSize), inner);  

    if(inner < 0.1) discard;    


    vec3 final = mix(color.rgb, uBorderColor, border);

    gl_FragColor = vec4(final, a);

    // gl_FragColor.rgb = vec3(inner);
  }
`;

// TODO: https://lygia.xyz/filter/noiseBlur
export default function GlassCard({ children, i, transform, getTexture }) {
  const background = useRef();
  const group = useRef();

  const texture = howWeDoItRenderTexture();

  const tBlurNoise = useTexture(noiseTex.src);
  const tBlurNoise2 = useTexture(noiseTex2.src);

  // const [tBackgroundColor, tMatcap, tNormal] = useTexture([
  //   color_outerspace.src,
  //   matcapTex.src,
  //   normalTex.src,
  // ]);

  // tBackgroundColor.wrapS = tBackgroundColor.wrapT = RepeatWrapping;
  tBlurNoise.wrapS = tBlurNoise.wrapT = RepeatWrapping;
  tBlurNoise2.wrapS = tBlurNoise2.wrapT = RepeatWrapping;
  const geometry = useMemo(() => new PlaneGeometry(1, 1, 32, 32), []);

  useEffect(() => {
    background.current.set("tScene", texture);
  }, [texture]);

  const theme = useTheme();

  const uniforms = useMemo(
    () => ({
      uSize: {
        value: new Vector2(transform.rect.width, transform.rect.height),
      },

      uBorderColor: {
        value: new Color(theme.theme.textColor).convertLinearToSRGB(),
        __colorName: "textColor",
      },
      // tBackgroundColor: {
      //   // TODO:
      //   value: tBackgroundColor,
      // },
      // tNormal: {
      //   value: tNormal,
      // },
      // tMatcap: {
      //   value: tMatcap,
      // },
      tBlurNoise: {
        value: tBlurNoise,
      },
      tBlurNoise2: {
        value: tBlurNoise2,
      },
      tScene: { value: null },
      tScenePrev: { value: null },
    }),
    [theme]
  );

  useThemeTransitionGLUniforms(
    () => background?.current?.mesh,
    ["uBorderColor"],
    uniforms
  );

  useResize(
    () => {
      uniforms.uSize.value = new Vector2(
        transform.rect.width,
        transform.rect.height
      );
    },
    { immediate: true },
    [transform]
  );

  useFrame(() => {
    background.current.set("tScene", getTexture());
    //background.current.set("tScenePrev", getTexture());
  });

  return (
    <group
      ref={group}
      scale={[transform.scale.x, transform.scale.y, transform.scale.z * 520]}
      position={[transform.position.x, 0, transform.position.z]}
    >
      {children}
      <ShaderObject
        ref={background}
        visible
        vertexShader={vertexShader}
        fragmentShader={fragmentShader}
        uniforms={uniforms}
        geometry={geometry}
        renderOrder={100}
      />
    </group>
  );
}
